/**
 * OpenAI Service
 * 
 * Handles all OpenAI API interactions.
 * This service is kept separate from other AI services because it has a different API structure.
 */

import { ApiKeyService } from '../apiKeyService';
import { TokenCountingService } from '../tokenCountingService';
import { logger } from '../loggingService';

interface OpenAIMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

interface GenerateCompletionOptions {
  prompt: string;
  model: string;
  temperature?: number;
  max_tokens?: number;
  userApiKeyId?: string;
}

export interface GenerateCompletionResponse {
  content: string;
  inputTokens: number;
  outputTokens: number;
}

export interface OpenAIModel {
  id: string;
  name: string;
  description?: string;
  context_window?: number;
}

export class OpenAIService {
  private static async getApiKey(userApiKeyId?: string): Promise<string | null> {
    if (userApiKeyId) {
      const decryptedKey = await ApiKeyService.getDecryptedApiKey(userApiKeyId);
      if (decryptedKey) {
        return decryptedKey;
      }
    }

    const platformKey = import.meta.env.VITE_OPENAI_API_KEY;
    return platformKey || null;
  }

  static async generateCompletion(options: GenerateCompletionOptions): Promise<GenerateCompletionResponse> {
    const apiKey = await this.getApiKey(options.userApiKeyId);

    if (!apiKey) {
      throw new Error('OpenAI API key not configured. Please add your API key in Settings.');
    }

    const messages: OpenAIMessage[] = [
      { role: 'user', content: options.prompt },
    ];

    const estimatedInputTokens = TokenCountingService.estimateMessagesTokens(
      messages,
      'openai',
      options.model
    );

    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model: options.model,
        messages,
        temperature: options.temperature ?? 0.7,
        max_tokens: options.max_tokens || 4096,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(`OpenAI API error: ${error.error?.message || response.statusText}`);
    }

    const result = await response.json();

    return {
      content: result.choices[0]?.message?.content || '',
      inputTokens: result.usage?.prompt_tokens || estimatedInputTokens,
      outputTokens: result.usage?.completion_tokens || 0,
    };
  }

  static async testConnection(apiKey: string): Promise<boolean> {
    try {
      const response = await fetch('https://api.openai.com/v1/models', {
        headers: {
          'Authorization': `Bearer ${apiKey}`,
        },
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Fetch available models from OpenAI API
   */
  static async getAvailableModels(userApiKeyId?: string): Promise<OpenAIModel[]> {
    const apiKey = await this.getApiKey(userApiKeyId);

    if (!apiKey) {
      throw new Error('OpenAI API key not configured.');
    }

    try {
      const response = await fetch('https://api.openai.com/v1/models', {
        headers: {
          'Authorization': `Bearer ${apiKey}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch models: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Filter to only chat models and sort by relevance
      const chatModels = (data.data || []).filter((model: any) => 
        model.id.includes('gpt') || model.id.includes('o1')
      );

      return chatModels.map((model: any) => ({
        id: model.id,
        name: model.id,
        description: `OpenAI ${model.id}`,
        context_window: model.context_window,
      }));
    } catch (error) {
      logger.error('Error fetching OpenAI models:', error);
      throw error;
    }
  }

  /**
   * Get available models by API key directly (for connection setup)
   */
  static async getAvailableModelsByApiKey(apiKey: string): Promise<OpenAIModel[]> {
    try {
      const response = await fetch('https://api.openai.com/v1/models', {
        headers: {
          'Authorization': `Bearer ${apiKey}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch models: ${response.statusText}`);
      }

      const data = await response.json();
      
      const chatModels = (data.data || []).filter((model: any) => 
        model.id.includes('gpt') || model.id.includes('o1')
      );

      return chatModels.map((model: any) => ({
        id: model.id,
        name: model.id,
        description: `OpenAI ${model.id}`,
        context_window: model.context_window,
      }));
    } catch (error) {
      logger.error('Error fetching OpenAI models:', error);
      throw error;
    }
  }

  static async executeWithApiKey(apiKey: string, model: string, prompt: string): Promise<string> {
    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: model,
          messages: [{ role: 'user', content: prompt }],
          temperature: 0.7,
          max_tokens: 4096,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(`OpenAI API error: ${error.error?.message || response.statusText}`);
      }

      const result = await response.json();
      return result.choices[0]?.message?.content || '';
    } catch (error) {
      logger.error('Error executing with OpenAI API:', error);
      throw error;
    }
  }

  /**
   * Generate code with OpenAI
   */
  static async generateCode(options: {
    prompt: string;
    language: string;
    userApiKeyId?: string;
    temperature?: number;
  }): Promise<GenerateCompletionResponse> {
    const codePrompt = `Generate ${options.language} code for the following requirement:\n\n${options.prompt}\n\nProvide only the code, no explanations.`;
    
    return this.generateCompletion({
      prompt: codePrompt,
      model: 'gpt-4-turbo-preview',
      temperature: options.temperature ?? 0.3,
      max_tokens: 4096,
      userApiKeyId: options.userApiKeyId,
    });
  }

  /**
   * Analyze code with OpenAI
   */
  static async analyzeCode(options: {
    code: string;
    analysisType: 'review' | 'bugs' | 'security' | 'performance';
    userApiKeyId?: string;
  }): Promise<GenerateCompletionResponse> {
    const analysisPrompts = {
      review: 'Perform a comprehensive code review of the following code. Identify issues, suggest improvements, and provide best practices:',
      bugs: 'Analyze the following code for potential bugs, edge cases, and logical errors:',
      security: 'Perform a security audit of the following code. Identify vulnerabilities, security risks, and provide remediation suggestions:',
      performance: 'Analyze the following code for performance issues, optimization opportunities, and efficiency improvements:',
    };

    const prompt = `${analysisPrompts[options.analysisType]}\n\n\`\`\`\n${options.code}\n\`\`\``;

    return this.generateCompletion({
      prompt,
      model: 'gpt-4-turbo-preview',
      temperature: 0.5,
      max_tokens: 4096,
      userApiKeyId: options.userApiKeyId,
    });
  }

  /**
   * Chat with streaming support
   */
  static async generateCompletionStream(
    options: GenerateCompletionOptions,
    onChunk: (chunk: string) => void
  ): Promise<GenerateCompletionResponse> {
    const apiKey = await this.getApiKey(options.userApiKeyId);

    if (!apiKey) {
      throw new Error('OpenAI API key not configured. Please add your API key in Settings.');
    }

    const messages: OpenAIMessage[] = [
      { role: 'user', content: options.prompt },
    ];

    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model: options.model,
        messages,
        temperature: options.temperature ?? 0.7,
        max_tokens: options.max_tokens || 4096,
        stream: true,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(`OpenAI API error: ${error.error?.message || response.statusText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Stream not available');
    }

    const decoder = new TextDecoder();
    let fullContent = '';
    let inputTokens = 0;
    let outputTokens = 0;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n').filter(line => line.trim() !== '');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;

            try {
              const parsed = JSON.parse(data);
              const content = parsed.choices[0]?.delta?.content;
              
              if (content) {
                fullContent += content;
                onChunk(content);
              }

              if (parsed.usage) {
                inputTokens = parsed.usage.prompt_tokens;
                outputTokens = parsed.usage.completion_tokens;
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    return {
      content: fullContent,
      inputTokens: inputTokens || TokenCountingService.estimateMessagesTokens(messages, 'openai', options.model),
      outputTokens: outputTokens || TokenCountingService.estimateTokenCount(fullContent),
    };
  }
}

// Export singleton instance for backward compatibility
export const openaiService = new (class {
  async generateCompletion(options: GenerateCompletionOptions): Promise<GenerateCompletionResponse> {
    return OpenAIService.generateCompletion(options);
  }
  async testConnection(apiKey: string): Promise<boolean> {
    return OpenAIService.testConnection(apiKey);
  }
  async getAvailableModels(userApiKeyId?: string): Promise<OpenAIModel[]> {
    return OpenAIService.getAvailableModels(userApiKeyId);
  }
  async getAvailableModelsByApiKey(apiKey: string): Promise<OpenAIModel[]> {
    return OpenAIService.getAvailableModelsByApiKey(apiKey);
  }
  async executeWithApiKey(apiKey: string, model: string, prompt: string): Promise<string> {
    return OpenAIService.executeWithApiKey(apiKey, model, prompt);
  }
  async generateCode(options: { prompt: string; language: string; userApiKeyId?: string; temperature?: number }): Promise<GenerateCompletionResponse> {
    return OpenAIService.generateCode(options);
  }
  async analyzeCode(options: { code: string; analysisType: 'review' | 'bugs' | 'security' | 'performance'; userApiKeyId?: string }): Promise<GenerateCompletionResponse> {
    return OpenAIService.analyzeCode(options);
  }
  async generateCompletionStream(options: GenerateCompletionOptions, onChunk: (chunk: string) => void): Promise<GenerateCompletionResponse> {
    return OpenAIService.generateCompletionStream(options, onChunk);
  }
})();

